import React, { useState } from "react";
import ReactModal from "react-modal";
import Editor from "react-simple-code-editor";
import { highlight, languages } from "prismjs";

import "prismjs/themes/prism.css";
import "./prism-freshpaint.css";

import "./CodeEditorModal.css";

import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";

export type CodeEditorModalProps = {
  onCancel: () => void;
  onSave: (code: string) => void;
  parentElementID: string;
  initialCode: string;
  textPrompt: React.ReactNode;
  saveEnabled: boolean;
};

const modalStyles = {
  content: {
    zIndex: 10050,
    minHeight: `100px`,
    maxHeight: "90vh",
    width: 600,
    top: "auto",
    bottom: "auto",
    right: "auto",
    left: "auto",
    position: "static" as "static",
    display: "flex",
    padding: 0,
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, .5)",
    zIndex: 10000,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};

const useStyles = makeStyles((theme) => ({
  cancelBtn: {
    background: theme.palette.background.default,
  },
  card: {
    width: "100%",
  },
  cardActions: {
    padding: theme.spacing(2),
  },
  cardContent: {
    maxHeight: "60vh",
    overflowY: "auto",
    padding: theme.spacing(2),
  },
  cardHeader: {
    backgroundColor: theme.palette.grey[50],
    borderBottomColor: theme.palette.grey[200],
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    padding: theme.spacing(2),
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  modalActions: {
    alignContent: "center",
    justifyContent: "right",
  },
}));

export function CodeEditorModal({
  onCancel,
  onSave,
  parentElementID,
  initialCode,
  textPrompt,
  saveEnabled,
}: CodeEditorModalProps) {
  const classes = useStyles();
  const [code, setCode] = useState<string>(initialCode);

  function handleSave() {
    onSave(code);
  }

  function handleCancel() {
    onCancel();
  }

  return (
    <ReactModal
      appElement={document.getElementById("root") as HTMLElement}
      parentSelector={() =>
        document.getElementById(parentElementID) as HTMLElement
      }
      isOpen={true}
      onRequestClose={onCancel}
      style={modalStyles}
      contentLabel="Code Editor"
    >
      <Card className={classes.card}>
        <CardHeader className={classes.cardHeader} title="Code Editor" />

        <Divider />

        <CardContent className={classes.cardContent}>
          <Editor
            className="border-0 bg-gray-100 text-gray-900 mb-4 font-mono text-xs"
            padding={12}
            highlight={(code) => highlight(code, languages.js, "JavaScript")}
            style={{ minHeight: 120 }}
            autoFocus={true}
            onValueChange={(code) => setCode(code)}
            value={code}
          />

          <pre
            style={{
              backgroundColor: "linen",
              fontSize: "12px",
              marginBottom: "1em",
              padding: 3,
            }}
          >
            <code
              className="language-javascript border-0 bg-blue-100 text-gray-900 mb-4 font-mono text-xs"
              style={{ fontSize: "12px" }}
            >
              {textPrompt}
            </code>
          </pre>
        </CardContent>

        <CardActions className={classes.cardActions}>
          <Grid container className={classes.modalActions} spacing={1}>
            <Grid item>
              <Button
                className={classes.cancelBtn}
                color="inherit"
                size="small"
                title="Cancel"
                variant="contained"
                onClick={() => {
                  handleCancel();
                }}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="primary"
                disabled={!saveEnabled}
                size="small"
                title="save"
                variant="contained"
                onClick={handleSave}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </CardActions>
      </Card>
    </ReactModal>
  );
}
